/**
 * Audit Summary Component
 * 
 * Displays a summary card with key audit information including
 * who submitted, who actioned, and any recalls.
 */

import type { AuditSummary as AuditSummaryType } from '@/types/audit';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { BarChart3, UserCheck, Undo2 } from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';

interface AuditSummaryProps {
  summary: AuditSummaryType;
}

export function AuditSummary({ summary }: AuditSummaryProps) {
  const formatRelative = (date?: Date) => {
    if (!date) return 'N/A';
    return formatDistanceToNow(date, { addSuffix: true });
  };

  const formatAbsolute = (date?: Date) => {
    if (!date) return '';
    return format(date, 'MMM d, yyyy h:mm a');
  };

  return (
    <Card className="mb-4">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <BarChart3 className="h-5 w-5" />
          Summary
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2.5 text-sm">

        {/* Created */}
        {summary.createdAt && (
          <div className="flex justify-between gap-2">
            <span className="text-muted-foreground flex-shrink-0">Created:</span>
            <span className="font-medium text-right">
              {formatRelative(summary.createdAt)}
              {summary.createdBy && <> by {summary.createdBy}</>}
            </span>
          </div>
        )}

        {/* Last Modified */}
        {summary.lastModifiedAt && (
          <div className="flex justify-between gap-2">
            <span className="text-muted-foreground flex-shrink-0">Last Modified:</span>
            <span className="font-medium text-right">
              {formatRelative(summary.lastModifiedAt)}
              {summary.lastModifiedBy && <> by {summary.lastModifiedBy}</>}
            </span>
          </div>
        )}

        {/* Submitted */}
        {summary.submittedAt && (
          <div className="flex justify-between gap-2">
            <span className="text-muted-foreground flex-shrink-0">Submitted:</span>
            <span className="font-medium text-right">
              {formatRelative(summary.submittedAt)}
              {summary.submittedBy && <> by {summary.submittedBy}</>}
              <span className="block text-[10px] text-muted-foreground font-normal">
                {formatAbsolute(summary.submittedAt)}
              </span>
            </span>
          </div>
        )}

        {/* Recalled */}
        {summary.recalledAt && (
          <div className="flex justify-between gap-2">
            <span className="text-muted-foreground flex-shrink-0 flex items-center gap-1">
              <Undo2 className="h-3 w-3" />
              Recalled:
            </span>
            <span className="font-medium text-amber-700 dark:text-amber-400 text-right">
              {formatRelative(summary.recalledAt)}
              {summary.recalledBy && <> by {summary.recalledBy}</>}
              {summary.recallReason && (
                <span className="block text-[10px] text-muted-foreground font-normal mt-0.5">
                  Reason: {summary.recallReason}
                </span>
              )}
            </span>
          </div>
        )}

        {/* Approved */}
        {summary.approvedAt && (
          <div className="flex justify-between gap-2">
            <span className="text-muted-foreground flex-shrink-0">Approved:</span>
            <span className="font-medium text-green-600 dark:text-green-400 text-right">
              {summary.approvalIsDelegated && summary.approvalDelegatedFromManagerName ? (
                <span className="flex flex-col items-end gap-0.5">
                  <span className="flex items-center gap-1">
                    <UserCheck className="h-3 w-3 text-blue-500 flex-shrink-0" />
                    <span>
                      {formatRelative(summary.approvedAt)} by{' '}
                      <span className="text-foreground font-semibold">{summary.approvedBy}</span>
                    </span>
                  </span>
                  <span className="text-[10px] text-blue-600 dark:text-blue-400 font-normal">
                    on behalf of <span className="font-semibold">{summary.approvalDelegatedFromManagerName}</span>
                  </span>
                  <span className="text-[10px] text-muted-foreground font-normal">
                    {formatAbsolute(summary.approvedAt)}
                  </span>
                </span>
              ) : (
                <>
                  {formatRelative(summary.approvedAt)}
                  {summary.approvedBy && <> by {summary.approvedBy}</>}
                  <span className="block text-[10px] text-muted-foreground font-normal">
                    {formatAbsolute(summary.approvedAt)}
                  </span>
                </>
              )}
            </span>
          </div>
        )}

        {/* Rejected */}
        {summary.rejectedAt && (
          <div className="flex justify-between gap-2">
            <span className="text-muted-foreground flex-shrink-0">Rejected:</span>
            <span className="font-medium text-red-600 dark:text-red-400 text-right">
              {summary.rejectionIsDelegated && summary.rejectionDelegatedFromManagerName ? (
                <span className="flex flex-col items-end gap-0.5">
                  <span className="flex items-center gap-1">
                    <UserCheck className="h-3 w-3 text-blue-500 flex-shrink-0" />
                    <span>
                      {formatRelative(summary.rejectedAt)} by{' '}
                      <span className="text-foreground font-semibold">{summary.rejectedBy}</span>
                    </span>
                  </span>
                  <span className="text-[10px] text-blue-600 dark:text-blue-400 font-normal">
                    on behalf of <span className="font-semibold">{summary.rejectionDelegatedFromManagerName}</span>
                  </span>
                  <span className="text-[10px] text-muted-foreground font-normal">
                    {formatAbsolute(summary.rejectedAt)}
                  </span>
                </span>
              ) : (
                <>
                  {formatRelative(summary.rejectedAt)}
                  {summary.rejectedBy && <> by {summary.rejectedBy}</>}
                  <span className="block text-[10px] text-muted-foreground font-normal">
                    {formatAbsolute(summary.rejectedAt)}
                  </span>
                </>
              )}
            </span>
          </div>
        )}

        {/* Total audit entries count */}
        <div className="flex justify-between pt-2 border-t">
          <span className="text-muted-foreground">Total Recorded Events:</span>
          <span className="font-semibold">{summary.totalChanges}</span>
        </div>

      </CardContent>
    </Card>
  );
}
